import {
  Component,
  DestroyRef,
  OnInit,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  computed,
  WritableSignal,
  signal,
  Input,
  input,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectionRange } from '@progress/kendo-angular-dateinputs';
import { FilterDescriptor, FilterOperator } from '@progress/kendo-data-query';
import { TranslateService } from '@ngx-translate/core';
import { ViewResponseModel } from '../../../CommonFiles/BusinessModels';
import {
  ColumnSettings,
  CustomColumnDescriptor,
  FilterDescriptorDDLs,
} from '../../../CommonFiles/UIModels';
import { UserGRPCService } from '../../../ProxyServices/user.grpc.service';
import { ViewConfigGRPCService } from '../../../ProxyServices/view-config.grpc.service';
import {
  FilterValueType,
  FilterValueTypes,
} from '../../../constants/filterValueTypes';
import { WorkorderGRPCService } from '../../../ProxyServices/workorder.grpc.service';
import {
  WOStatusOptions,
  WOStatusWebName,
} from '../../../constants/woStatusOptions';
import { UtilsDate } from '../../../OFramework/utilsDate';
import { faChevronDown, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faPlus, faSave } from '@fortawesome/pro-solid-svg-icons';
import { OAuthService } from '@olameter/o-authentication';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { SharedUtilService } from '../../../CommonFiles/utilities/shared-util.service';
import { WOBlackout } from 'src/app/CommonFiles/enums/workOrderDetails.enum';

export enum NonAvailableFields {
  InstallDate = 'InstallDate',
}

interface QueuedPastedValues {
  field: string;
  values: string[];
}

interface TranslatedFilterValueType extends FilterValueType {
  text: string;
}

interface PlannedDateFilterOption {
  text: string;
  value: string;
}

@Component({
  standalone: false,
  selector: 'app-master-filter',
  templateUrl: './master-filter.component.html',
  styleUrls: ['./master-filter.component.scss'],
})
export class MasterFilterComponent implements OnInit {
  @ViewChildren('multiselect') multiSelects!: QueryList<MultiSelectComponent>;
  static initialisedComponent: MasterFilterComponent;
  private readonly destroyRef = inject(DestroyRef);
  faChevronDown = faChevronDown;
  faPlus = faPlus;
  faSave = faSave;
  faTrashCan = faTrashCan;
  fieldOperators = FilterOperator;
  @Input() hideAnchor: any;
  active = input<ViewResponseModel>();

  woStatusOptionInitial = JSON.parse(JSON.stringify(WOStatusOptions));
  woStatusOptionSelected = JSON.parse(JSON.stringify(WOStatusOptions)); //TODO4398feu this should come in the RX
  woStatusOptionDefault = JSON.parse(JSON.stringify(WOStatusOptions));
  public columnsConfig: ColumnSettings[];
  filterTypes: TranslatedFilterValueType[] = [];
  plannedDateFilter: PlannedDateFilterOption[] = [];
  item = {
    text: '',
    operator: 'eq',
    isString: false,
  };
  public Filters: FilterDescriptorDDLs[];
  queuedPastedValues: QueuedPastedValues[] = [];
  woStatusOptionsFiltered = JSON.parse(JSON.stringify(WOStatusOptions));
  selectAll: boolean = true;
  public selectedPlannedDateFilter: PlannedDateFilterOption = {
    text: '',
    value: 'All',
  };
  public viewList: ViewResponseModel[];
  private statusFilter: any;
  @Output() filterEvent: EventEmitter<boolean> = new EventEmitter();
  @Output() save: EventEmitter<boolean> = new EventEmitter();

  plannedInstallDates: WritableSignal<SelectionRange> = signal({
    start: new Date(Date.now()),
    end: new Date(Date.now()),
  });
  selectedContract: WritableSignal<ViewResponseModel> = signal(null);
  workorderStatusCount: WritableSignal<number> = signal(0);

  public selectedItems: string[] = [];
  preFilterList: CustomColumnDescriptor[] = [];
  private filterInitialized: boolean = false;
  loadingFilterData: boolean = false;

  disabled = computed<boolean>(() => {
    const contract = this.selectedContract();
    const plannedInstallDates = this.plannedInstallDates();
    const { start, end } = plannedInstallDates;
    const workorderStatusCount = this.workorderStatusCount();

    // workorderStatusCount === 0
    return (
      contract === null ||
      start > end ||
      end < start ||
      workorderStatusCount === 0
    );
  });

  popupSettings = {
    popupAlign: { horizontal: 'right', vertical: 'top' },
    anchorAlign: { horizontal: 'left', vertical: 'bottom' },
  };

  constructor(
    protected _clientWO: WorkorderGRPCService,
    private _clientViewConf: ViewConfigGRPCService,
    private _clientUser: UserGRPCService,
    private authService: OAuthService,
    private translate: TranslateService
  ) {
    if (MasterFilterComponent.initialisedComponent) {
      MasterFilterComponent.initialisedComponent.refreshTranslatedOptions();
      return MasterFilterComponent.initialisedComponent;
    }

    this.refreshTranslatedOptions();
  }

  ngOnDestroy(): void {
    this.woStatusOptionDefault = this.woStatusOptionSelected.filter(
      (x) => x.active
    );

    MasterFilterComponent.initialisedComponent = this;
  }

  ngOnInit(): void {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshTranslatedOptions();
      });

    if (!MasterFilterComponent.initialisedComponent) {
      this._clientUser.subscribe((userInfo) => {
        if (userInfo?.length > 0) {
          const u = userInfo[0];
          this._clientViewConf.get(u); //to ignite the call for data
        }
      });
      this._clientUser.getAsyncByEmail(this.authService.userName()); //make call to the server to start everything

      this._clientViewConf.RXsubscribeToCurrentActiveView((value) => {
        if (value) {
          if (value.reloadDataFromBE || !this.filterInitialized) {
            //Only set when activeview need data reload from BE or on first init
            this.filterInitialized = true;
            const toSelect = value.toSelect;

            this.Filters =
              toSelect?.GridState?.filter?.filters?.map(
                (filter: FilterDescriptor) => {
                  const value =
                    typeof filter.value === 'string'
                      ? this.decodeFilterValue(filter.value)
                      : filter.value.map((item) =>
                          this.decodeFilterValue(item)
                        );
                  return new FilterDescriptorDDLs({ ...filter, value });
                }
              ) || [];

            this.statusFilter = toSelect?.WoStatus;

            this.initWoStatusOptions();

            const viewListTMP = this._clientViewConf.getValue();
            if (viewListTMP?.length > 0) {
              //remove duplicated contract entries
              const uniqueContracts = new Set();
              this.viewList = viewListTMP
                .filter((item) => {
                  const duplicate = uniqueContracts.has(item.contractName);
                  uniqueContracts.add(item.contractName);
                  return !duplicate;
                })
                .sort((a, b) => {
                  return a.contractTitle.localeCompare(b.contractTitle);
                });
            }

            const contract = this.viewList.find(
              (x) => x.contractName == toSelect?.contractName
            );
            this.selectedContract.set(contract);

            if (MasterFilterComponent.initialisedComponent) {
              let dates = this.plannedInstallDates();
              dates = {
                start: toSelect?.startDateFilter
                  ? toSelect.startDateFilter
                  : dates.start,
                end: toSelect?.endDateFilter
                  ? toSelect.endDateFilter
                  : dates.end,
              };
              this.plannedInstallDates.set(dates);
            }

            this.columnsConfig = toSelect?.ColumnsConfig;
          }
        }

        if (this.columnsConfig) {
          this.preFilterList = [];
          this.columnsConfig.forEach((i_columnConfig) => {
            if (i_columnConfig.field == WOStatusWebName.Field) {
              i_columnConfig.title = WOStatusWebName.Title;
            } else if (i_columnConfig.field === WOBlackout.BlackoutRemainingDays) {

              this.preFilterList.push({
                field: i_columnConfig.field,
                title: 'Blackout Countdown Days',
                checked: false,
              });
            }else if (
              i_columnConfig.field !== NonAvailableFields.InstallDate
            ) {
              this.preFilterList.push({
                field: i_columnConfig.field,
                title: i_columnConfig.title,
                checked: false,
              });
            } 

            this.Filters ||= [];
          });

          //if there exists a filter , select the field in columns config
          this.Filters?.forEach((item) => {
            let field = item.field;
            if (field == 'StatusLastChange') field = 'EventDate';
            this.preFilterList.find((value) => value.field === field).checked =
              true;
          });
        }
      });

      const plannedInstallDates = UtilsDate.CalculateDatesStartingMonday(
        this.plannedInstallDates().start
      );
      this.plannedInstallDates.set(plannedInstallDates);
      this.initWoStatusOptions();
    }
  }

  private refreshTranslatedOptions(): void {
    this.filterTypes = FilterValueTypes.map((filterType) => ({
      ...filterType,
      text: this.translate.instant(filterType.textKey),
    }));

    this.item =
      this.filterTypes.find((filterType) => filterType.operator === 'eq') ??
      this.item;

    const selectedValue = this.selectedPlannedDateFilter?.value ?? 'All';
    this.plannedDateFilter = this.buildPlannedDateFilters();
    this.selectedPlannedDateFilter =
      this.plannedDateFilter.find((filter) => filter.value === selectedValue) ??
      this.plannedDateFilter[0];
  }

  private buildPlannedDateFilters(): PlannedDateFilterOption[] {
    return [
      {
        text: this.translate.instant('HOME_COMPONENTS.MASTER_FILTER.ALL'),
        value: 'All',
      },
      {
        text: this.translate.instant('HOME_COMPONENTS.MASTER_FILTER.CUSTOM'),
        value: 'Custom',
      },
      {
        text: this.translate.instant('HOME_COMPONENTS.MASTER_FILTER.NULL'),
        value: 'Null',
      },
    ];
  }

  decodeFilterValue(input: string): string {
    let decoded = input;
    try {
      while (decoded !== decodeURIComponent(decoded)) {
        decoded = decodeURIComponent(decoded);
      }
      return decoded;
    } catch {
      return decoded;
    }
  }

  private initWoStatusOptions() {
    this.woStatusOptionsFiltered = JSON.parse(
      JSON.stringify(this.woStatusOptionInitial)
    );

    if (this.statusFilter == null) {
      this.woStatusOptionSelected = JSON.parse(
        JSON.stringify(this.woStatusOptionInitial)
      );
    } else {
      var values = this.statusFilter.value;
      this.woStatusOptionSelected.forEach((obj) => {
        if (values.findIndex((x) => obj.value == x) > -1) {
          obj.active = true;
        } else {
          obj.active = false;
        }
      });
    }

    this.woStatusOptionDefault = this.woStatusOptionSelected.filter(
      (x) => x.active
    );
    var filter = ViewResponseModel.MapperWoStatusToFilter(
      this.woStatusOptionSelected
    );
    this._clientViewConf.updateWorkOrderStatusFilter(filter);
    this.workorderStatusCount.set(this.woStatusOptionDefault.length);
  }

  contractChange(contract) {
    this.selectedContract.set(contract);

    this._clientWO.RXselectedWOIDsShared.next([]); //Selected WOID's has to be cleared when we switch between contracts
    // WOID's has to be cleared when we switch between contracts
    // When user switch contracts rectangle and polygon selection tool won't work
    this._clientWO.next([]);
    const viewListTMP = this._clientViewConf.getValue();
    const viewToSelect = ViewConfigGRPCService.getLastSeenFromList(
      viewListTMP,
      contract.contractName
    );

    this.filterInitialized = false;
    this._clientViewConf.RXSetCurrentActiveView(
      viewToSelect.name,
      false,
      this._clientViewConf.getValue(),
      true
    );
  }

  onChangeStartDate(value: Date) {
    const dates = this.plannedInstallDates();
    this.plannedInstallDates.set({ start: value, end: dates.end });
  }

  onChangeEndDate(value: Date) {
    const dates = this.plannedInstallDates();
    this.plannedInstallDates.set({ start: dates.start, end: value });
  }

  plannedDateFilterChange(e) {
    let plannedInstallDates = {
      start: null,
      end: null,
    };
    if (e.value === 'Custom') {
      plannedInstallDates = {
        start: new Date(Date.now()),
        end: new Date(Date.now()),
      };
    }
    this.plannedInstallDates.set(plannedInstallDates);
    this.selectedPlannedDateFilter = e;
  }

  public clearFilters(): void {
    //reset the planned install dates , WOstatus , selected WOID's , Filters , uncheck the prefilter fields
    this._clientWO.RXselectedWOIDsShared.next([]);

    this.onChangeStartDate(new Date(Date.now()));

    this.Filters = [];
    this.preFilterList.forEach((obj) => {
      obj.checked = false;
    });

    this.statusFilter = null;
    this.initWoStatusOptions();

    this._clientViewConf.RXSetCurrentActiveView(
      this._clientViewConf.getCurrentActiveView().name,
      false,
      this._clientViewConf.getValue(),
      true
    );
  }

  applyFilters() {
    let viewToSelect: ViewResponseModel = this.getViewToSelect();
    this._clientViewConf.RXSetCurrentActiveView(viewToSelect.name, true, [
      viewToSelect,
    ]);

    this.filterEvent.emit(true); //to avoid listenig in the action-component... if we need to listen in the future then this should be removed
  }

  filterTypesClicked(event, item: FilterDescriptor) {
    let filterVal = this.Filters.find(
      (x: FilterDescriptor) => x.field === item.field
    );

    filterVal.operator = event.operator;

    if (this.isFiltertypeWithoutValue(event.operator)) {
      filterVal.value = '';
    }
  }

  public isFiltertypeWithoutValue(operator) {
    switch (operator) {
      case FilterOperator.IsEmpty:
      case FilterOperator.IsNotEmpty:
        return true;
    }

    return false;
  }

  getTextbasedonFieldName(f) {
    return this.columnsConfig.find((x) => x.field === f)?.title;
  }

  getTypePrefilter(item) {
    if (item.field == 'StatusLastChange') item.field = 'EventDate';
    let retVal = this.columnsConfig.find((x) => x.field === item.field);
    return retVal.filter;
  }

  getTextForFilterDropDown(key): string {
    return (
      this.filterTypes.find((x) => x.operator === key)?.text ??
      this.filterTypes[0]?.text ??
      ''
    );
  }

  columnCheckChange(cc) {
    cc.forEach((item) => {
      this.updateFilterList(item, cc);
    });
  }

  restoreColPrefilter(item) {
    this.preFilterList.forEach((obj) => {
      if (obj.field == item.field) obj.checked = false;
    });
    let filterVal = this.Filters.find((x) => x.field === item.field);
    let indexFilterVal = this.Filters.indexOf(filterVal);
    this.Filters.splice(indexFilterVal, 1);
  }

  loadDataPrefilter(e) {
    const filterVal = this.Filters.find((x) => x.field === e.field);
    if (!(filterVal?.dropDownListSource?.length > 0)) {
      this.loadingFilterData = true;
      this._clientWO
        .getPreFilterColumnValues(
          e.field,
          '' /*e.value  //always load all the data and filter it in client*/,
          this.selectedContract().contractName,
          this.Filters,
          this.columnsConfig,
          (this.selectedPlannedDateFilter as any)?.value
        )
        .subscribe((List: string[]) => {
          this.loadingFilterData = false;

          // Filter out any null, empty, or undefined values
          const filteredList = List.filter(
            (item) =>
              item != null &&
              item !== '' &&
              item !== undefined &&
              item.trim() !== ''
          );

          filterVal.dropDownListSource = filteredList.sort(
            Intl.Collator().compare
          ); // sort by ignoring the case
          filterVal.dropDownList = filterVal.dropDownListSource.slice();
        });
    }
  }

  public handleChangePrefilter(filter: any, field): void {
    const filterVal = this.Filters.find((x) => x.field === field);
    filterVal.dropDownList = filterVal.dropDownListSource?.filter(
      (s) => s.toLowerCase().indexOf(filter.toLowerCase()) !== -1
    );
  }

  public handleChangeWOStatus(filter: any): void {
    this.woStatusOptionsFiltered = WOStatusOptions.filter(
      (x) => x.text.toLowerCase().indexOf(filter.toLowerCase()) !== -1
    );
  }

  onChangeWOStatus(event) {
    this.woStatusOptionSelected.forEach((obj) => {
      if (
        event.findIndex(
          (x) => obj.value.toLowerCase() == x.value.toLowerCase()
        ) > -1
      ) {
        obj.active = true;
      } else {
        obj.active = false;
      }
    });

    this.selectAll =
      this.woStatusOptionSelected.findIndex((x) => x.active) > -1;
    var filter = ViewResponseModel.MapperWoStatusToFilter(
      this.woStatusOptionSelected
    );
    this._clientViewConf.updateWorkOrderStatusFilter(filter);

    this.workorderStatusCount.set(
      this.woStatusOptionSelected.filter((x) => x.active).length
    );
  }

  toggleSelectAll() {
    this.woStatusOptionSelected = SharedUtilService.toggleSelectAll(
      WOStatusOptions,
      this.selectAll
    );
    this.woStatusOptionDefault = this.woStatusOptionSelected.filter(
      (x) => x.active
    );

    this.workorderStatusCount.set(
      this.woStatusOptionSelected.filter((x) => x.active).length
    );

    var filter = ViewResponseModel.MapperWoStatusToFilter(
      this.woStatusOptionSelected
    );
    this._clientViewConf.updateWorkOrderStatusFilter(filter);
  }

  valueChangePrefilter(value: any, field: string | Function): void {
    const filterVal = this.Filters.find((x) => x.field === field);
    if (filterVal) {
      filterVal.value =
        typeof value === 'string'
          ? this.decodeFilterValue(value)
          : value.map((item) => this.decodeFilterValue(item));
    }

    let viewToSelect: ViewResponseModel = this.getViewToSelect();
    this._clientViewConf.setCurrentActiveViewState(
      viewToSelect.GridState,
      viewToSelect
    );
  }

  private updateFilterList(item, cc = []) {
    let index = cc.findIndex((x) => x.field === item.field);
    const filterVal = this.Filters.find((x) => x.field === item.field);
    //add only if the filter doesnt already exist
    if (cc[index].checked && !filterVal) {
      this.Filters.push({
        field: item.field,
        value: item.value ? item.value : '',
        operator: FilterOperator.EqualTo,
      }); //next is done only when button is actionated
    } //Remove if it is unchecked from ColumnsConfig popup
    else if (!cc[index].checked && filterVal) {
      let indexFilterVal = this.Filters.indexOf(filterVal);
      this.Filters.splice(indexFilterVal, 1);
    }
  }

  private getViewToSelect(): ViewResponseModel {
    let viewToSelect: ViewResponseModel =
      this._clientViewConf.getCurrentActiveView();
    viewToSelect.installDateOption = (
      this.selectedPlannedDateFilter as any
    )?.value;
    viewToSelect.GridState.filter = {
      logic: 'and',
      filters: FilterDescriptorDDLs.mapToFilterDescriptors(this.Filters),
    };
    const dateFilter = this.plannedInstallDates();
    viewToSelect.endDateFilter =
      dateFilter.end == null ? new Date(Date.now()) : dateFilter.end;
    viewToSelect.startDateFilter =
      dateFilter.start == null ? new Date(Date.now()) : dateFilter.start;

    return viewToSelect;
  }

  // Paste event
  onPaste(event: ClipboardEvent, index: number) {
    // Prevent default to not allow pasted value to appear in the input field.
    event.preventDefault();

    // Pre defined pipe to split multiple pasted values
    const pipe = /\r?\n/;
    if (event.type === 'paste') {
      // Get pasted value
      const pastedData = event.clipboardData?.getData('text');
      // Split the value using pre defined pipe
      const filterValues = pastedData
        .split(pipe)
        .filter((item) => item !== undefined && item !== '');

      // Get selected filter
      const filter = this.Filters[index];
      // If values are loaded from server then process the pasted values
      const value = Array.from(new Set([...filter.value, ...filterValues]));
      filter.value = value;
    }
  }
  onEnter(index: number) {
    const multi = this.multiSelects.toArray()[index];
    const input = multi.wrapper.nativeElement.querySelector(
      'input.k-input-inner'
    ) as HTMLInputElement;
    if (input) {
      input.value = '';
      input.dispatchEvent(new Event('input')); // Trigger Angular change detection
    }
  }

  saveView() {
    const { isDefault } = this.active();
    this.save.emit(isDefault);
  }
}
